import { useTranslation } from 'react-i18next';
import { FlagBR, FlagES, FlagFR, FlagGB } from '@/components/common/FlagIcon';
import { Button } from '@/components/ui/button';
import { APP_LANGUAGES, getAppLanguage, type AppLanguageCode } from '@/i18n/languages';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';

const FLAG_BY_LANGUAGE: Record<AppLanguageCode, typeof FlagFR> = {
  fr: FlagFR,
  en: FlagGB,
  es: FlagES,
  pt: FlagBR,
};

const LANGUAGES = APP_LANGUAGES.map(({ code, nativeLabel }) => ({
  code,
  label: nativeLabel,
  Icon: FLAG_BY_LANGUAGE[code],
}));

export default function LangSwitcher() {
  const { t, i18n } = useTranslation('common');
  const current = getAppLanguage(i18n.resolvedLanguage || i18n.language).code;
  const activeLanguage = LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[0];
  const ActiveIcon = activeLanguage.Icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="
            h-9 px-2 rounded-[var(--radius-md)]
            bg-white/5 text-white
            border border-white/20
            hover:bg-white/15 hover:border-white/40 hover:text-white
            focus-visible:ring-2 focus-visible:ring-white/40
          "
          aria-label={t('changeLanguage')}
        >
          <ActiveIcon className="rounded-xs" />
          <span className="text-sm font-medium">{activeLanguage.label}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={8} className="min-w-40">
        {LANGUAGES.map(({ code, label, Icon }) => (
          <DropdownMenuItem
            key={code}
            onClick={() => i18n.changeLanguage(code)}
            className={current === code ? 'font-medium' : ''}
          >
            <Icon className="mr-2 h-4 w-4 rounded-[2px]" />
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
